import React, { useEffect, useState } from 'react'
import { billingApi } from '../../services/billing'
import { Screen } from '../../components/lease/Screen'
import { BrandHeading } from '../../components/lease/BrandHeading'
import { PrimaryButton } from '../../components/lease/PrimaryButton'

// Landlord pricing — filters the shared /v1/billing/plans catalog down to
// the landlord-audience tiers (Single $69, Property Manager Pack $294).
// Visual pattern borrowed from features/purchase/PurchaseOptions.jsx; the
// buy flow (acceptTerms -> checkout -> redirect) mirrors pages/legal/LegalGate.jsx.
export function LandlordPricing() {
  const [plans, setPlans] = useState(null)
  const [loadError, setLoadError] = useState(null)
  const [busyTier, setBusyTier] = useState(null)
  const [checkoutError, setCheckoutError] = useState(null)

  useEffect(() => {
    billingApi.getPlans().then(setPlans).catch((e) => setLoadError(e.message))
  }, [])

  const handleBuy = async (tier) => {
    setBusyTier(tier)
    setCheckoutError(null)
    try {
      await billingApi.acceptTerms()
      const { checkout_url } = await billingApi.checkout(tier)
      window.location.assign(checkout_url)
    } catch (err) {
      // PAYMENTS_ENABLED is currently false on the backend — checkout 503s.
      setCheckoutError(
        err.response?.status === 503
          ? 'Checkout is opening soon — check back shortly.'
          : err.response?.data?.detail || 'Something went wrong. Try again.'
      )
      setBusyTier(null)
    }
  }

  if (loadError) {
    return (
      <Screen className="text-center">
        <p className="text-slate-600 dark:text-slate-300">Could not load pricing. Try again shortly.</p>
      </Screen>
    )
  }

  if (!plans) {
    return (
      <Screen className="text-center">
        <p className="text-slate-600 dark:text-slate-300">Loading pricing…</p>
      </Screen>
    )
  }

  const tiers = Object.entries(plans).filter(([, p]) => p.audience === 'landlord')

  return (
    <Screen>
      <header className="animate-fade-in text-center">
        <BrandHeading className="text-4xl">Landlord Pricing</BrandHeading>
        <p className="mt-3 text-slate-600 dark:text-slate-300">
          Compliance review and redline draft, ready to send to your attorney.
        </p>
      </header>

      <div className="mt-8 space-y-4">
        {tiers.map(([tier, p]) => (
          <div
            key={tier}
            className="animate-slide-in rounded-2xl bg-white/70 p-6 shadow-sm ring-1 ring-emerald-100 backdrop-blur dark:bg-slate-800/60 dark:ring-slate-700"
          >
            <h3 className="text-xl font-bold text-slate-800 dark:text-slate-100">{p.name}</h3>
            <p className="mt-1 text-3xl font-bold text-emerald-700 dark:text-emerald-400">${p.price_usd}</p>
            <ul className="mt-3 space-y-1 text-sm text-slate-600 dark:text-slate-300">
              {p.features?.map((f) => (
                <li key={f}>✓ {f}</li>
              ))}
            </ul>
            <PrimaryButton onClick={() => handleBuy(tier)} disabled={busyTier === tier} className="mt-4">
              {busyTier === tier ? 'One moment…' : `Buy ${p.name}`}
            </PrimaryButton>
          </div>
        ))}
      </div>

      {checkoutError && (
        <p className="mt-4 text-center text-sm text-rose-600 dark:text-rose-400">{checkoutError}</p>
      )}
    </Screen>
  )
}

export default LandlordPricing
